/**
 * This patch fixes corrupt detailPage filters that have a non-existing collection id in their `detailPage` filter.
 */

import * as DomTypeUtils from '@packages/systems/core/utils/DomTypeUtils';
import _ from 'lodash';
import type {Context} from '../types';

export default (context: Context) => {
  const {
    node: {
      jsonNode: {data: nodeData = {}, _id},
    },
    extraContext: {collections},
  } = context;

  const hasDataWHrefBinding =
    DomTypeUtils.isDynBound(nodeData, 'dataWHref') &&
    Array.isArray(nodeData.dyn.bind.dataWHref);

  const firstBindingPathFragment: any =
    hasDataWHrefBinding && _.first(nodeData.dyn.bind.dataWHref);
  const lastBindingPathFragment: any =
    hasDataWHrefBinding && _.last(nodeData.dyn.bind.dataWHref);
  const firstFragmentIsItemRefField =
    firstBindingPathFragment && firstBindingPathFragment.type === 'ItemRef';
  const lastFragmentIsSlugField =
    lastBindingPathFragment &&
    lastBindingPathFragment.id === 'slug' &&
    lastBindingPathFragment.slug === 'slug';
  const isItemRefDetailPageLink =
    firstFragmentIsItemRefField && lastFragmentIsSlugField;

  const filter = lastBindingPathFragment && lastBindingPathFragment.filter;
  const filterParts = typeof filter === 'string' && filter.split('|');
  const filterName = filterParts && filterParts[0];
  const collectionId = filterParts && filterParts[1];
  const collection = collections && collectionId && collections[collectionId];

  const isCorruptLink =
    isItemRefDetailPageLink &&
    filterName === 'detailPage' &&
    collections &&
    !collection;

  const contextCollection =
    isCorruptLink &&
    collections &&
    firstBindingPathFragment &&
    _.find(
      collections,
      (coll) =>
        coll &&
        coll.fields &&
        coll.fields.some(
          (field) =>
            field &&
            field.type === 'ItemRef' &&
            field.id === firstBindingPathFragment.id &&
            field.slug === firstBindingPathFragment.slug
        )
    );

  const itemRefField =
    contextCollection &&
    firstBindingPathFragment &&
    contextCollection.fields &&
    contextCollection.fields.find(
      (field: any) =>
        field &&
        field.type === 'ItemRef' &&
        field.id === firstBindingPathFragment.id &&
        field.slug === firstBindingPathFragment.slug
    );

  const itemRefTargetCollectionId =
    itemRefField &&
    itemRefField.validations &&
    itemRefField.validations.collectionId &&
    // It is a Mongo ObjectID so we want to call `toString()` and use that.
    _.isFunction(itemRefField.validations.collectionId.toString) &&
    itemRefField.validations.collectionId.toString();

  const replacementCollectionIdFound =
    typeof itemRefTargetCollectionId === 'string';

  return {
    needed: replacementCollectionIdFound,
    logData: {
      msg: 'dataWHref-bound itemRef link has a corrupt detailPage filter',
      meta: {
        nodeId: _id,
      },
    },
    apply: () => {
      // This is already checked in `needed` above but we need this for Flow type check to pass.
      if (
        lastBindingPathFragment &&
        typeof itemRefTargetCollectionId === 'string'
      ) {
        const newFilter = `detailPage|${itemRefTargetCollectionId}`;
        lastBindingPathFragment.filter = newFilter;
      }
    },
  };
};
